import type React from "react";
import { useEffect, useRef } from "react";

type FireworksProps = {
	trigger: boolean;
	onComplete: () => void;
};

export const Fireworks: React.FC<FireworksProps> = ({
	trigger,
	onComplete,
}) => {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);

	useEffect(() => {
		if (trigger) {
			const canvas = canvasRef.current;
			const ctx = canvas?.getContext("2d");
			let particles: {
				x: number;
				y: number;
				radius: number;
				color: string;
				speedX: number;
				speedY: number;
				alpha: number;
			}[] = [];
			let animationFrameId: number;
			const particleCount = 100;
			const colors = ["#AE58FF", "#FF6F61", "#4CAF50", "#FFD700"];

			if (!ctx || !canvas) return;

			const resizeCanvas = () => {
				canvas.width = window.innerWidth;
				canvas.height = window.innerHeight;
			};
			resizeCanvas();
			window.addEventListener("resize", resizeCanvas);

			const createParticles = (x: number, y: number) => {
				for (let i = 0; i < particleCount; i++) {
					const angle = Math.random() * 1 * Math.PI;
					const speed = Math.random() * 6;
					particles.push({
						x,
						y,
						radius: Math.random() * 2 + 2,
						color: colors[Math.floor(Math.random() * colors.length)],
						speedX: Math.cos(angle) * speed,
						speedY: Math.sin(angle) * speed,
						alpha: 1,
					});
				}
			};

			const updateParticles = () => {
				ctx.clearRect(0, 0, canvas.width, canvas.height);
				particles = particles.filter((p) => p.alpha > 0);

				for (const p of particles) {
					p.x += p.speedX;
					p.y += p.speedY;
					p.alpha -= 0.01;
					ctx.beginPath();
					ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2, false);
					ctx.fillStyle = `rgba(${hexToRGB(p.color)}, ${p.alpha})`;
					ctx.fill();
				}

				if (particles.length === 0) {
					cancelAnimationFrame(animationFrameId);
					onComplete();
				}
			};

			const animate = () => {
				updateParticles();
				animationFrameId = requestAnimationFrame(animate);
			};

			const triggerFireworks = () => {
				const centerX = canvas.width / 2;
				const centerY = canvas.height / 2;
				createParticles(centerX, centerY);
				animate();
			};

			if (trigger) {
				triggerFireworks();
			}

			return () => {
				window.removeEventListener("resize", resizeCanvas);
				cancelAnimationFrame(animationFrameId);
			};
		}
	}, [trigger, onComplete]);

	const hexToRGB = (hex: string) => {
		const bigint = Number.parseInt(hex.slice(1), 16);
		const r = (bigint >> 16) & 255;
		const g = (bigint >> 8) & 255;
		const b = bigint & 255;
		return `${r},${g},${b}`;
	};

	return (
		<canvas
			ref={canvasRef}
			style={{ position: "fixed", top: 0, left: 0, zIndex: 999 }}
		/>
	);
};
